import { IField } from '../form';
import { ILoadingStageModel } from '../loadingStage';
import { TranslationNode, TranslationString } from '../localization';
import { TariffKind } from '../tariff';

import { CalculatePayError, PaymentAddition, PaymentParams } from './server';

export interface IPaymentInfo {
  readonly monthPrice: number;
  readonly minimalPayment: number;
  readonly maxUsersCount: number | null;
  readonly usersCount: number;
  readonly billing: IBilling;
  readonly shouldPay: boolean;
  readonly trialDaysLeft: number;
  readonly daysLeft: number;
  readonly isTrialOver: boolean;
  readonly nextPaymentDate: Date;
  readonly state: PaymentState;
  readonly optimalTariff: TariffKind;
  readonly isSubscriptionActive: boolean;
  readonly bankruptDate: Date;
  readonly invoiceAutogeneratedDate: Date | null;
  readonly paymentsCount: number | null;
}

export interface IPaymentMethod {
  id: string;
  title: string;
  type: string;
  dateCreated: Date;
}

export interface IBilling {
  /** Суммарный баланс для UI (balance + legal_entity_balance из API) */
  balance: number;
  /** Сырой юр. баланс */
  legalEntityBalance: number | null;
  trialUntil: Date;
  tariff: TariffKind;
}

export enum PaymentState {
  subscriptionAvailable = 'subscription_available',
  subscriptionActive = 'subscription_active',
  subscriptionStopped = 'subscription_stopped',
  receivingMoneyNotAvailable = 'receiving_money_not_available'
}

export const mapMaxUsersCountToString: Record<number, string> = {
  1000: '< 1 000',
  2000: '1 000 - 2 000',
  5000: '2 000 - 5 000',
  10000: '5 000 - 10 000',
  30000: '10 000 - 30 000',
  50000: '30 000 - 50 000',
  100000: '50 000 - 100 000',
  1000000: '100 000 - ∞'
};

export type InfoMessageType =
  | {
      isError: false;
      data: TranslationString;
    }
  | {
      isError: true;
      data: TranslationNode;
      code: CalculatePayError | null;
    }
  | null;

export interface IPaymentFill {
  readonly selectedMonthCount: IField<number | null>;
  readonly promoCode: IField<string>;
  readonly amount: IField<string>;
  readonly priceToPay: IField<number | null>;
  readonly calculatingPriceStage: ILoadingStageModel;
  readonly payStage: ILoadingStageModel;
  readonly addition: IField<PaymentAddition>;
  readonly testPeriodPromoCodeSelected: boolean;
  readonly paymentParams: PaymentParams;
  readonly calculateResult: IField<PaymentCalculateResult | null>;

  pay(): Promise<BaseResponse>;
  onPayByCardButtonClick: () => Promise<BaseResponse>;
  applyPromoCode(): Promise<BaseResponse>;
  resetPromoCode(): void;
}

export enum PaymentForAIState {
  notTrialNotElamaPositive = 'notTrial_notElama_positive', // Баланс >0 И НЕ триал и не Elama
  notTrialNotElamaZero = 'notTrial_notElama_zero', // Баланс == 0 И НЕ триал и не Elama
  trialZeroFreeAI = 'trial_zero_freeAI', // Триал, баланс 0, бот работает на бесплатном ИИ
  trialZeroPaidAI = 'trial_zero_paidAI', // Триал, баланс 0, выбрана платная модель
  trialPositive = 'trial_Positive', // триал, баланс >0
  elamaActiveTrialZero = 'elama_activeTrial_zero', // Elama: АКТИВНЫЙ триал, баланс 0 (любая модель)
  elamaNotActiveTrial = 'elama_notActiveTrial', // Elama: НЕактивный триал, любой баланс, любая модель
  elamaActiveTrialPositive = 'elama_activeTrial_positive' // Elama: АКТИВНЫЙ триал, баланс > 0 (любая модель)
}

export type PaymentCalculateResult = {
  originalPrice: number;
  priceToPay: number;
  discountPerMonths: {
    discount: number;
    numberOfMonths: number;
  } | null;
  discountForPromocode: {
    discount: number;
    promocode: string;
  } | null;
};

export enum PaymentInfoState {
  isCorporate = 'is_corporate', // корп тариф
  trialAndPositiveBalance = 'trial_and_positive_balance', //триал с положительным балансом
  trialAndZeroBalance = 'trial_and_zero_balance', //триал с 0
  active = 'active', // не триал и есть деньги
  inactive = 'inactive' // не триал и 0
}

export type PaymentStateParams =
  | {
      state: PaymentInfoState.isCorporate;
      trialUntilDate: string;
    }
  | {
      state: PaymentInfoState.inactive;
    }
  | {
      state: PaymentInfoState.active;
      nextPaymentDate: string;
      daysLeft: number;
      bankruptDate: string;
    }
  | {
      state: PaymentInfoState.trialAndPositiveBalance;
      trialUntilDate: string;
      trialDaysLeft: number;
      nextPaymentDate: string;
      daysLeft: number;
      bankruptDate: string;
    }
  | {
      state: PaymentInfoState.trialAndZeroBalance;
      trialUntilDate: string;
      trialDaysLeft: number;
    };
